import { FeatureFlag, PackageTier, AddOnType, UpgradeItemType } from 'fourwaves-shared';

export default {
  plans: {
    upgrade: 'Upgrade',
    addon: 'Add-On',
    purchased: 'Already Purchased',
    per_year: '/ year',
    year: '{count}-year',
    ending_plan_alert: {
      title: 'Your Plan will end in {count} day. | Your Plan will end in {count} days.',
      message: 'To retain your benefits, upgrade your account before {date}.',
    },
    overdue_plan_alert: {
      title: 'Your invoice is overdue.',
      message: 'To retain your benefits, please make the payment by {date}.',
      cta: 'View Invoice',
    },
    addon_name: {
      [AddOnType.PeerReviewsModule]: 'Peer-review Module',
      [AddOnType.VirtualPosterSessions]: 'Virtual Platform',
      [AddOnType.VideoRecordings]: 'Video Recordings',
      [AddOnType.UnlimitedSubmissions]: 'Unlimited Submissions',
      [AddOnType.PaymentCollection]: ' Payment Collection Service',
    },
    addon_description: {
      [AddOnType.PeerReviewsModule]:
        'Identify the top scored presentations by your program committee and share results to authors.',
      [AddOnType.VirtualPosterSessions]:
        'Offer an engaging virtual space with livestreaming, dynamic virtual poster session and more.',
      [AddOnType.VideoRecordings]:
        'Allow authors to submit a video recording of their presentation or share session recordings, available after the conference.',
      [AddOnType.UnlimitedSubmissions]:
        'Benefit from an unlimited number of submissions for your event without the need to upgrade to a higher plan.',
      [AddOnType.PaymentCollection]:
        "If you don't have a payment gateway account (e.g., Stripe, PayPal), we collect payments for you and make a bank transfer after the event.",
    },
    addon_link: {
      [AddOnType.PeerReviewsModule]: 'https://fourwaves.com/peer-review-software/',
      [AddOnType.VirtualPosterSessions]: 'https://fourwaves.com/virtual-poster-sessions/',
      [AddOnType.VideoRecordings]: '',
      [AddOnType.UnlimitedSubmissions]: '',
      [AddOnType.PaymentCollection]: '',
    },
    addon_feature_label: {
      live_app_1: 'Connect to any livestream service or use Zoom',
      live_app_2: 'Live chat and Q&A',
      live_app_3: 'Virtual poster session with video conversations',
      live_app_4: 'Event chat, activity feed',
      live_app_5: 'Private messaging',
      review_1: 'Import your list of evaluation committee members',
      review_2: 'Create a custom evaluation form',
      review_3: 'Distribute submissions to committee members',
      review_4: 'Select the highest-rated submissions',
      review_5: 'Share feedback with authors',
    },
    tier_name: {
      [PackageTier.Free]: 'Free Tier',
      [PackageTier.Essentials]: 'Essential Tier',
      [PackageTier.Advanced]: 'Advanced Tier',
      [PackageTier.Professional]: 'Pro Tier',
    },
    package_name: {
      [PackageTier.Free]: 'Free',
      [PackageTier.Essentials]: 'Essential',
      [PackageTier.Advanced]: 'Advanced',
      [PackageTier.Professional]: 'Pro',
    },
    package_feature_label: {
      [FeatureFlag.MassEmails]: 'Email Communication',
      [FeatureFlag.EnhancedContentBlocks]: 'Premium Content Blocks',
      [FeatureFlag.Badges]: 'Name Tags Generator',
      [FeatureFlag.ContentPages]: 'Complete Event Website',
      [FeatureFlag.PrivateContent]: 'Participant-Only Content',
      [FeatureFlag.UnlimitedSubmissions]: 'Unlimited Abstract submissions / event',
      [FeatureFlag.WhiteLabel]: 'White Labeled Website',
      free_plus: 'Everything in Free, and:',
      essential_plus: 'Everything in Essential, and:',
      advanced_plus: 'Everything in Advanced, and:',
      hundred_abstracts: 'Up to 100 Abstracts submissions / event',
      two_hundred_abstracts: 'Up to 200 Abstracts submissions / event',
      onboarding: 'Onboarding Call + Pre-Launch Checkup',
      vip_support: 'Unlimited VIP Support',
      peer_review: 'Peer-review Module',
      video: 'Video Recordings',
      api: 'API Access',
      fees: '{count}% Transaction Fee',
      volume_fees: 'Volume-Based Transaction Fee',
      unlimited_registrations: 'Unlimited Registrations',
      twenty_five_abstracts: 'Up to 25 Abstracts submissions / event',
      base_ews: 'Basic Event Website',
    },
    package_feature_tooltip: {
      fees: {
        [PackageTier.Free]: 'Payment gateway fees apply.',
        [PackageTier.Essentials]: 'Payment gateway fees apply.',
        [PackageTier.Advanced]: 'Payment gateway fees apply.',
        [PackageTier.Professional]:
          'Can be negotiated down depending on your payment volume. Payment gateway fees also apply.',
      },
      [FeatureFlag.PrivateContent]:
        'Allows certain pages of the website to be accessible to registered participants only.',
    },
    cta: {
      package: 'Upgrade',
      addon: 'Buy Add-On',
      renew: 'Renew',
    },
    upgrade_modal: {
      no_admin_title: "Looks like you're not an admin",
      no_admin_message:
        "In order to upgrade your plan or purchase add-ons, you'll need to contact your organization admin.",
      contact_admin: 'Please contact your admin to upgrade.',
      event_limit_title: 'Unable to Publish Event',
      event_limit_message:
        'Your organization has reached the limit of active events for your current plan. To publish this event, please upgrade your plan or wait for an existing event to conclude.',
      [FeatureFlag.MassEmails]: {
        title: 'Unlock the email communication feature',
        message:
          "This feature is only available with the Essential Plan and higher. Upgrade now to keep your participants fully informed about your event's updates.",
      },
      [FeatureFlag.Badges]: {
        title: 'Unlock the Name Tags Generator feature',
        message:
          'This feature is only available with the Essential Plan and higher. Upgrade now to generate a PDF of your name tags, ready to be printed.',
      },
      [FeatureFlag.AbstractBooklet]: {
        title: 'Unlock the Abstract Booklet feature',
        message:
          'This feature is only available with the Advanced Plan and higher. Upgrade now to generate a full, customizable booklet of your abstract booklet.',
      },
      [FeatureFlag.Reviewing]: {
        title: 'Unlock the Peer-review Module',
        message: 'This module is part of the Peer-review add-on. Purchase now to streamline your Peer-review process.',
      },
    },
    alert: {
      content_pages: `This feature is available with the Advanced Plan and above. Upgrade now to add new pages to your event website.`,
      content_block: 'Some content blocks are only available from the Essential Plan.',
      content_block_menu: `This feature is available with the Essential Plan and above. Upgrade now to access all types of content blocks.`,
      page_visibility: `This feature is available with the Advanced Plan and above. Upgrade now to restrict this page visibility to registrants only.`,
      confirmation_emails: `This feature is available with the Essential Plan and above. Upgrade now to customize the confirmation email.`,
      video_form:
        'The video field is part of the Video Recordings add-on. Purchase now to unlock video form field type.',
      video_schedule:
        'The session recordings is part of the Video Recordings add-on. Purchase now to upload and share session recordings.',
      unlimited_registrations: 'Your current plan allows for Unlimited Registrations.',
      unlimited_submissions: 'Your current plan allows for Unlimited Submissions.',
      limited_submissions: 'Your current plan allows for {count} Submissions.',
      limited_registrations: 'Your current plan allows for {count} Registrations.',
      submissions_limit_warning: "You're approaching your current plan's submission limit for this event.",
      submissions_limit_reached: "You've reached your submission limit for this event under your current plan.",
      submissions_limit_warning_message: 'Once reached, no further submissions can be received.',
      submissions_limit_reached_message: 'No further submissions can be received.',
      mass_emails_message:
        'This feature is available with the Essential Plan and above. Upgrade now to keep your participants informed of event updates.',
      review_message:
        'This module is part of the Peer-review add-on. Purchase now to streamline your abstract submission process.',
      vps_message:
        'This module is part of the Virtual Platform add-on. Purchase now to unlock livestreaming, virtual poster sessions, and more.',
    },
    pricing_modal: {
      title: 'Cost Breakdown',
    },
    pricing: {
      title: 'Summary',
      per_event: 'x {count} event | x {count} events',
      rebate_item: {
        [UpgradeItemType.CurrentPlanCredit]: 'Credit from current plan',
        [UpgradeItemType.MultiYearDiscount]: 'Multi year discount 10%',
        [UpgradeItemType.MultiEventDiscount]: 'Multi event discount 10%',
        [UpgradeItemType.IncludedInPackage]: 'Included',
      },
      annual_invoice_date: 'Next invoice: {date}',
      terms_of_use: 'Terms of Use',
      terms_of_use_link: 'https://fourwaves.com/terms-of-use/',
      terms_of_service: 'Terms of Service',
      terms_of_service_link: 'https://fourwaves.com/terms-of-service/',
      terms: 'You agree to the {termOfUse} and the {termOfService}.',
      terms_renew:
        "Your plan will automatically renew annually until end of term. We'll remind you to log in and make the payment. You also agree to the {termOfUse} and the {termOfService}.",
    },
    tile: {
      current_plan: 'Current plan',
      free: 'Free',
      new_plan: 'New plan',
      term: 'Term',
      tier: 'Tier',
      events: 'Events',
      addons: 'Add-ons',
      none: 'None',
    },
  },
};
